import React, { useState, useEffect, useMemo } from 'react';
import {
  Paper,
  Typography,
  Box,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  IconButton,
  Tooltip
} from '@mui/material';
import { Refresh, Settings, Fullscreen, FullscreenExit, Save, DragIndicator } from '@mui/icons-material';
import {
  saveFacilityToStorage,
  loadFacilityFromStorage,
  formatFacilityOptions
} from '../../utils/facilityStorage';
import { useDashboardData } from './useDashboardData';

// Import simple dashboard navigation widgets
import DriverDashboardWidget from './widgets/DriverDashboardWidget';
import DockDashboardWidget from './widgets/DockDashboardWidget';
import YardDashboardWidget from './widgets/YardDashboardWidget';
import TasksDashboardWidget from './widgets/TasksDashboardWidget';
import DriverBoardDashboardWidget from './widgets/DriverBoardDashboardWidget';
import DriverCommsWidget from './widgets/DriverCommsWidget';
import EmptyTrailersByScacWidget from './widgets/EmptyTrailersByScacWidget';
import BolDashboardWidget from './widgets/BolDashboardWidget';

// GraphQL API URL
const GRAPHQL_API_URL = process.env.REACT_APP_GRAPHQL_API || 'http://localhost:4000/api/graphql';

// Interface for facility
interface Facility {
  id: number;
  name: string;
}

// Widget configuration interface
interface WidgetConfig {
  id: string;
  name: string;
  component: React.ComponentType<any>;
  enabled: boolean;
  span: number; // Grid span (1-12)
  order: number;
  height: 'default' | 'extended' | 'extended+'; // Height configuration
}

// Dashboard now uses simple navigation widgets that link to existing pages

const Dashboard = () => {
  // State for facilities
  const [facilities, setFacilities] = useState<Facility[]>([]);
  const [selectedFacility, setSelectedFacilityState] = useState<Facility | null>(() => {
    return loadFacilityFromStorage();
  });

  // Wrapper function to save facility selection to localStorage
  const setSelectedFacility = (facility: Facility | null) => {
    setSelectedFacilityState(facility);
    saveFacilityToStorage(facility);
  };

  // Dashboard state
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showSettings, setShowSettings] = useState(false);
  const [saving, setSaving] = useState(false);
  const [compactMode, setCompactMode] = useState(() => {
    const saved = localStorage.getItem('dashboardCompactMode');
    return saved ? JSON.parse(saved) : false;
  });
  const [draggedWidget, setDraggedWidget] = useState<string | null>(null);
  const [dragOverWidget, setDragOverWidget] = useState<string | null>(null);

  // Widget config-dependent query params (tracked at dashboard level for consolidated query)
  const [commsSinceHours, setCommsSinceHours] = useState<number>(() => {
    const saved = localStorage.getItem('driverCommsWidget_sinceHours');
    return saved ? parseInt(saved, 10) : 24;
  });
  const [bolSinceHours, setBolSinceHours] = useState<number>(() => {
    const saved = localStorage.getItem('bolWidgetConfig');
    if (saved) {
      try { return JSON.parse(saved).sinceHours || 2; } catch { return 2; }
    }
    return 2;
  });

  // Widget configuration
  const [widgetConfigs, setWidgetConfigs] = useState<WidgetConfig[]>(() => {
    const saved = localStorage.getItem('dashboardWidgetConfigs');
    if (saved) {
      try {
        const savedConfigs = JSON.parse(saved);
        // Check if driver-comms widget exists in saved config, if not add it
        const hasDriverComms = savedConfigs.some((config: any) => config.id === 'driver-comms');
        if (!hasDriverComms) {
          savedConfigs.push({
            id: 'driver-comms',
            name: 'Driver Communications',
            component: DriverCommsWidget,
            enabled: false, // Default to disabled so user can enable it
            span: 6,
            order: 3,
            height: 'default'
          });
          // Re-sort by order
          savedConfigs.sort((a: any, b: any) => a.order - b.order);
        }
        
        // Reconstruct component mappings for saved configs
        const componentMap: Record<string, React.ComponentType<any>> = {
          'driver-mgmt': DriverDashboardWidget,
          'dock-mgmt': DockDashboardWidget,
          'driver-comms': DriverCommsWidget,
          'tasks': TasksDashboardWidget,
          'empty-trailers-scac': EmptyTrailersByScacWidget,
          'bol-dashboard': BolDashboardWidget
        };

        let configs = savedConfigs.map((savedConfig: any) => ({
          ...savedConfig,
          component: componentMap[savedConfig.id as keyof typeof componentMap] || savedConfig.component,
          height: savedConfig.height || 'default' // Ensure height is set for existing configs
        }));

        // Ensure empty-trailers-scac widget exists in saved config
        if (!configs.some((c: any) => c.id === 'empty-trailers-scac')) {
          configs.push({
            id: 'empty-trailers-scac',
            name: 'Empty Trailers by SCAC',
            component: EmptyTrailersByScacWidget,
            enabled: false,
            span: 3,
            order: configs.length + 1,
            height: 'default'
          });
        }

        // Ensure bol-dashboard widget exists in saved config
        if (!configs.some((c: any) => c.id === 'bol-dashboard')) {
          configs.push({
            id: 'bol-dashboard',
            name: 'BOL Awaiting Signature',
            component: BolDashboardWidget,
            enabled: false,
            span: 6,
            order: configs.length + 1,
            height: 'default'
          });
        }

        return configs;
      } catch (e) {
        console.error('Failed to parse saved widget configs:', e);
      }
    }

    // Default configuration - operational dashboard widgets
    return [
      { id: 'driver-mgmt', name: 'Driver Management', component: DriverDashboardWidget, enabled: true, span: 6, order: 1, height: 'default' },
      { id: 'dock-mgmt', name: 'Dock Management', component: DockDashboardWidget, enabled: true, span: 6, order: 2, height: 'default' },
      { id: 'driver-comms', name: 'Driver Communications', component: DriverCommsWidget, enabled: false, span: 6, order: 3, height: 'default' },
      { id: 'tasks', name: 'Task Management', component: TasksDashboardWidget, enabled: true, span: 6, order: 4, height: 'default' },
      { id: 'empty-trailers-scac', name: 'Empty Trailers by SCAC', component: EmptyTrailersByScacWidget, enabled: false, span: 3, order: 5, height: 'default' },
      { id: 'bol-dashboard', name: 'BOL Awaiting Signature', component: BolDashboardWidget, enabled: false, span: 6, order: 6, height: 'default' },
    ];
  });

  // Auto-refresh interval (30 seconds)
  const [autoRefresh, setAutoRefresh] = useState(() => {
    const saved = localStorage.getItem('dashboardAutoRefresh');
    return saved ? JSON.parse(saved) : true;
  });

  // Compute enabled widget IDs for the consolidated hook
  const enabledWidgetIds = useMemo(
    () => widgetConfigs.filter(c => c.enabled).map(c => c.id),
    [widgetConfigs]
  );

  // Consolidated data hook — single GraphQL request for all widgets
  const {
    data: dashboardData,
    loading: dashboardDataLoading,
    isRefreshing: dashboardIsRefreshing,
    error: dashboardDataError,
    refresh: refreshDashboardData,
  } = useDashboardData({
    selectedFacility,
    enabledWidgetIds,
    commsSinceHours,
    bolSinceHours,
  });

  // Save widget configs to localStorage
  useEffect(() => {
    localStorage.setItem('dashboardWidgetConfigs', JSON.stringify(widgetConfigs));
  }, [widgetConfigs]);

  // Save compact mode to localStorage
  useEffect(() => {
    localStorage.setItem('dashboardCompactMode', JSON.stringify(compactMode));
  }, [compactMode]);

  // Save auto refresh to localStorage
  useEffect(() => {
    localStorage.setItem('dashboardAutoRefresh', JSON.stringify(autoRefresh));
  }, [autoRefresh]);

  // Load layout from facility
  const loadLayoutFromFacility = async (facility: Facility) => {
    try {
      const authToken = document.cookie
        .split('; ')
        .find(row => row.startsWith('authToken='))
        ?.split('=')[1] || localStorage.getItem('authToken');

      const getQuery = `
        query GetFacility {
          facility(id: "${facility.id}") {
            id
            name
            configuration
          }
        }
      `;
      
      const response = await fetch(GRAPHQL_API_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${authToken}` },
        body: JSON.stringify({ query: getQuery })
      });
      
      const result = await response.json();
      if (result.errors) {
        console.error('Failed to load facility config:', result.errors[0].message);
        return;
      }

      const config = result.data.facility?.configuration ? 
        (typeof result.data.facility.configuration === 'string' ? 
          JSON.parse(result.data.facility.configuration) : 
          result.data.facility.configuration) : {};

      // Load dashboard configuration if it exists
      if (config.dashboard) {
        if (config.dashboard.widgetConfigs) {
          // Reconstruct component mappings
          const componentMap: Record<string, React.ComponentType<any>> = {
            'driver-mgmt': DriverDashboardWidget,
            'dock-mgmt': DockDashboardWidget,
            'driver-comms': DriverCommsWidget,
            'tasks': TasksDashboardWidget,
            'empty-trailers-scac': EmptyTrailersByScacWidget,
            'bol-dashboard': BolDashboardWidget
          };

          const reconstructedConfigs = config.dashboard.widgetConfigs.map((savedConfig: any) => ({
            ...savedConfig,
            component: componentMap[savedConfig.id as keyof typeof componentMap]
          }));

          // Ensure all configs have height property
          const configsWithHeight = reconstructedConfigs.map((config: any) => ({
            ...config,
            height: config.height || 'default'
          }));

          // Check if driver-comms widget exists in saved config, if not add it
          const hasDriverComms = configsWithHeight.some((widgetConfig: WidgetConfig) => widgetConfig.id === 'driver-comms');
          if (!hasDriverComms) {
            configsWithHeight.push({
              id: 'driver-comms',
              name: 'Driver Communications',
              component: DriverCommsWidget,
              enabled: true,
              span: 6,
              order: 3,
              height: 'default'
            });
          }

          // Check if empty-trailers-scac widget exists in saved config, if not add it
          if (!configsWithHeight.some((widgetConfig: WidgetConfig) => widgetConfig.id === 'empty-trailers-scac')) {
            configsWithHeight.push({
              id: 'empty-trailers-scac',
              name: 'Empty Trailers by SCAC',
              component: EmptyTrailersByScacWidget,
              enabled: false,
              span: 3,
              order: configsWithHeight.length + 1,
              height: 'default'
            });
          }

          // Check if bol-dashboard widget exists in saved config, if not add it
          if (!configsWithHeight.some((widgetConfig: WidgetConfig) => widgetConfig.id === 'bol-dashboard')) {
            configsWithHeight.push({
              id: 'bol-dashboard',
              name: 'BOL Awaiting Signature',
              component: BolDashboardWidget,
              enabled: false,
              span: 6,
              order: configsWithHeight.length + 1,
              height: 'default'
            });
          }

          // Re-sort by order
          configsWithHeight.sort((a: WidgetConfig, b: WidgetConfig) => a.order - b.order);

          setWidgetConfigs(configsWithHeight);
        }
        if (typeof config.dashboard.compactMode === 'boolean') {
          setCompactMode(config.dashboard.compactMode);
        }
        if (typeof config.dashboard.autoRefresh === 'boolean') {
          setAutoRefresh(config.dashboard.autoRefresh);
        }
      }
    } catch (err) {
      console.error('Failed to load dashboard layout:', err);
    }
  };

  // Fetch facilities
  useEffect(() => {
    const fetchFacilities = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const facilitiesQuery = `
          query {
            facilities {
              id
              name
            }
          }
        `;

        const authToken = document.cookie
          .split('; ')
          .find(row => row.startsWith('authToken='))
          ?.split('=')[1] || localStorage.getItem('authToken');

        const response = await fetch(GRAPHQL_API_URL, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authToken}`
          },
          body: JSON.stringify({
            query: facilitiesQuery
          })
        });

        const result = await response.json();
        
        if (result.errors) {
          throw new Error(result.errors[0].message);
        }

        setFacilities(result.data.facilities);
        
        // Validate localStorage facility still exists
        const storedFacility = loadFacilityFromStorage();
        if (storedFacility) {
          const facilityExists = result.data.facilities.find((f: any) => f.id === storedFacility.id);
          if (!facilityExists && result.data.facilities.length > 0) {
            setSelectedFacility(result.data.facilities[0]);
            // Load layout after state is set
            setTimeout(() => loadLayoutFromFacility(result.data.facilities[0]), 0);
          } else {
            // Load layout for existing facility
            setTimeout(() => loadLayoutFromFacility(storedFacility), 0);
          }
        } else if (result.data.facilities.length > 0) {
          setSelectedFacility(result.data.facilities[0]);
          // Load layout after state is set
          setTimeout(() => loadLayoutFromFacility(result.data.facilities[0]), 0);
        }
      } catch (err) {
        console.error('Failed to fetch facilities:', err);
        setError('Failed to fetch facilities');
      } finally {
        setLoading(false);
      }
    };
    
    fetchFacilities();
  }, []);

  // Auto-refresh effect — calls consolidated hook's refresh every 30s
  useEffect(() => {
    if (!autoRefresh || !selectedFacility) return;

    const interval = setInterval(() => {
      refreshDashboardData();
    }, 30000);

    return () => clearInterval(interval);
  }, [autoRefresh, selectedFacility, refreshDashboardData]);

  // Handle manual refresh
  const handleRefresh = () => {
    refreshDashboardData();
  };

  // Handle facility change
  const handleFacilityChange = (event: any) => {
    const facilityId = event.target.value;
    const facility = facilities.find(f => f.id === facilityId);
    if (facility) {
      setSelectedFacility(facility);
      loadLayoutFromFacility(facility);
    }
  };

  // Save layout to facility
  const handleSaveLayout = async () => {
    if (!selectedFacility) return;

    try {
      setSaving(true);
      setError(null);

      const authToken = document.cookie
        .split('; ')
        .find(row => row.startsWith('authToken='))
        ?.split('=')[1] || localStorage.getItem('authToken');

      // Get current facility config
      const getQuery = `
        query GetFacility {
          facility(id: "${selectedFacility.id}") {
            id
            name
            configuration
          }
        }
      `;
      
      const getResponse = await fetch(GRAPHQL_API_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${authToken}` },
        body: JSON.stringify({ query: getQuery })
      });
      
      const getResult = await getResponse.json();
      if (getResult.errors) {
        throw new Error(getResult.errors[0].message);
      }
      
      // Update config
      const existingConfig = getResult.data.facility?.configuration ? 
        (typeof getResult.data.facility.configuration === 'string' ? 
          JSON.parse(getResult.data.facility.configuration) : 
          getResult.data.facility.configuration) : {};
          
      // Save only serializable widget data (no component functions)
      const serializableWidgets = widgetConfigs.map(({ component, ...rest }) => rest);
      
      const updatedConfig = {
        ...existingConfig,
        dashboard: { 
          widgetConfigs: serializableWidgets, 
          compactMode, 
          autoRefresh 
        }
      };

      // Save config
      const saveQuery = `
        mutation UpdateFacility($id: ID!, $input: FacilityUpdateInput!) {
          update_facility(id: $id, input: $input) {
            id
            name
            configuration
          }
        }
      `;
      
      const saveResponse = await fetch(GRAPHQL_API_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${authToken}` },
        body: JSON.stringify({
          query: saveQuery,
          variables: { 
            id: selectedFacility.id, 
            input: { 
              name: getResult.data.facility.name,
              configuration: JSON.stringify(updatedConfig) 
            } 
          }
        })
      });

      const saveResult = await saveResponse.json();
      if (saveResult.errors) {
        throw new Error(saveResult.errors[0].message);
      }

      console.log('Dashboard layout saved successfully');
    } catch (err) {
      console.error('Save error:', err);
      setError('Failed to save layout');
    } finally {
      setSaving(false);
    }
  };

  // Toggle widget enabled state
  const toggleWidget = (widgetId: string) => {
    setWidgetConfigs(prev => 
      prev.map(config => 
        config.id === widgetId 
          ? { ...config, enabled: !config.enabled }
          : config
      )
    );
  };

  // Update widget span
  const updateWidgetSpan = (widgetId: string, span: number) => {
    setWidgetConfigs(prev => 
      prev.map(config => 
        config.id === widgetId 
          ? { ...config, span }
          : config
      )
    );
  };

  // Update widget height
  const updateWidgetHeight = (widgetId: string, height: 'default' | 'extended' | 'extended+') => {
    setWidgetConfigs(prev => 
      prev.map(config => 
        config.id === widgetId 
          ? { ...config, height }
          : config
      )
    );
  };

  // Drag and drop handlers
  const handleDragStart = (e: React.DragEvent, widgetId: string) => {
    setDraggedWidget(widgetId);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/html', widgetId);
  };

  const handleDragOver = (e: React.DragEvent, widgetId: string) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverWidget(widgetId);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOverWidget(null);
  };

  const handleDrop = (e: React.DragEvent, dropTargetId: string) => {
    e.preventDefault();
    
    if (!draggedWidget || draggedWidget === dropTargetId) {
      setDraggedWidget(null);
      setDragOverWidget(null);
      return;
    }

    setWidgetConfigs(prev => {
      const draggedIndex = prev.findIndex(config => config.id === draggedWidget);
      const dropTargetIndex = prev.findIndex(config => config.id === dropTargetId);
      
      if (draggedIndex === -1 || dropTargetIndex === -1) return prev;

      const newConfigs = [...prev];
      const draggedConfig = newConfigs[draggedIndex];
      const dropTargetConfig = newConfigs[dropTargetIndex];

      // Swap the order values
      const tempOrder = draggedConfig.order;
      draggedConfig.order = dropTargetConfig.order;
      dropTargetConfig.order = tempOrder;

      // Sort by order to maintain proper positioning
      newConfigs.sort((a, b) => a.order - b.order);

      return newConfigs;
    });

    setDraggedWidget(null);
    setDragOverWidget(null);
  };

  const handleDragEnd = () => {
    setDraggedWidget(null);
    setDragOverWidget(null);
  };

  // Get enabled widgets sorted by order
  const enabledWidgets = widgetConfigs
    .filter(config => config.enabled)
    .sort((a, b) => a.order - b.order);

  // Helper function to calculate widget height
  const getWidgetHeight = (heightConfig: 'default' | 'extended' | 'extended+', compactMode: boolean): number => {
    const baseHeight = compactMode ? 320 : 400;
    switch (heightConfig) {
      case 'extended':
        return Math.round(baseHeight * 1.5); // 50% more height
      case 'extended+':
        return baseHeight * 2; // 100% more height
      case 'default':
      default:
        return baseHeight;
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-screen">
        <Typography variant="h6" sx={{ mt: 2 }}>Loading dashboard...</Typography>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-screen">
        <Typography variant="h6" color="error" sx={{ mb: 2 }}>{error}</Typography>
      </div>
    );
  }

  return (
    <div className="p-5">
      {/* Header */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h4" component="h1">
            Operations Dashboard
          </Typography>
          
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            {/* Auto-refresh toggle */}
            <FormControlLabel
              control={
                <Switch
                  checked={autoRefresh}
                  onChange={(e) => setAutoRefresh(e.target.checked)}
                  color="primary"
                />
              }
              label="Auto-refresh"
            />

            {/* Compact mode toggle */}
            <FormControlLabel
              control={
                <Switch
                  checked={compactMode}
                  onChange={(e) => setCompactMode(e.target.checked)}
                  color="primary"
                />
              }
              label="Compact"
            />

            {/* Save layout button */}
            <Tooltip title="Save Layout">
              <IconButton
                onClick={handleSaveLayout}
                disabled={saving || !selectedFacility}
                color="primary"
              >
                <Save />
              </IconButton>
            </Tooltip>

            {/* Settings toggle */}
            <Tooltip title="Widget Settings">
              <IconButton
                onClick={() => setShowSettings(!showSettings)}
                color={showSettings ? "primary" : "default"}
              >
                <Settings />
              </IconButton>
            </Tooltip>

            {/* Manual refresh */}
            <Tooltip title="Refresh All Widgets">
              <IconButton
                onClick={handleRefresh}
                disabled={dashboardDataLoading}
                color="primary"
              >
                <Refresh className={dashboardIsRefreshing ? 'animate-spin' : ''} />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          {/* Facility Selector */}
          <FormControl variant="outlined" size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Facility</InputLabel>
            <Select
              value={selectedFacility?.id || ''}
              onChange={handleFacilityChange}
              label="Facility"
            >
              {facilities.map(facility => (
                <MenuItem key={facility.id} value={facility.id}>
                  {facility.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Typography variant="body2" color="text.secondary">
            {enabledWidgets.length} widgets active
            {autoRefresh && ' • Auto-refreshing every 30s'}
          </Typography>
        </Box>

        {/* Widget Configuration Panel */}
        {showSettings && (
          <Box sx={{ mt: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Widget Configuration
              <Typography variant="caption" display="block" color="text.secondary">
                Drag widgets to reorder them
              </Typography>
            </Typography>
            <Grid container spacing={2}>
              {widgetConfigs
                .sort((a, b) => a.order - b.order)
                .map(config => (
                <Grid item xs={12} sm={6} md={4} key={config.id}>
                  <Box 
                    sx={{ 
                      p: 2, 
                      border: 1, 
                      borderColor: dragOverWidget === config.id ? 'primary.main' : 'grey.300',
                      borderWidth: dragOverWidget === config.id ? 2 : 1,
                      borderStyle: dragOverWidget === config.id ? 'dashed' : 'solid',
                      borderRadius: 1,
                      bgcolor: draggedWidget === config.id ? 'action.hover' : 'background.paper',
                      opacity: draggedWidget === config.id ? 0.7 : 1,
                      cursor: 'move',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        borderColor: 'primary.light'
                      }
                    }}
                    draggable={true}
                    onDragStart={(e) => handleDragStart(e, config.id)}
                    onDragOver={(e) => handleDragOver(e, config.id)}
                    onDragLeave={handleDragLeave}
                    onDrop={(e) => handleDrop(e, config.id)}
                    onDragEnd={handleDragEnd}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <DragIndicator sx={{ color: 'text.secondary', mr: 1, fontSize: 18 }} />
                      <Typography variant="body2" fontWeight="medium" sx={{ flex: 1 }}>
                        #{config.order} {config.name}
                      </Typography>
                    </Box>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={config.enabled}
                          onChange={() => toggleWidget(config.id)}
                        />
                      }
                      label="Enabled"
                    />
                    {config.enabled && (
                      <Box sx={{ mt: 1 }}>
                        <Box sx={{ mb: 1 }}>
                          <Typography variant="caption">Width:</Typography>
                          <Select
                            size="small"
                            value={config.span}
                            onChange={(e) => updateWidgetSpan(config.id, e.target.value as number)}
                            sx={{ ml: 1, minWidth: 80 }}
                          >
                            <MenuItem value={3}>1/4</MenuItem>
                            <MenuItem value={4}>1/3</MenuItem>
                            <MenuItem value={6}>1/2</MenuItem>
                            <MenuItem value={8}>2/3</MenuItem>
                            <MenuItem value={12}>Full</MenuItem>
                          </Select>
                        </Box>
                        <Box>
                          <Typography variant="caption">Height:</Typography>
                          <Select
                            size="small"
                            value={config.height}
                            onChange={(e) => updateWidgetHeight(config.id, e.target.value as 'default' | 'extended' | 'extended+')}
                            sx={{ ml: 1, minWidth: 100 }}
                          >
                            <MenuItem value="default">Default</MenuItem>
                            <MenuItem value="extended">Extended</MenuItem>
                            <MenuItem value="extended+">Extended+</MenuItem>
                          </Select>
                        </Box>
                      </Box>
                    )}
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Box>
        )}
      </Paper>

      {/* Widget Grid */}
      <Grid container spacing={3}>
        {enabledWidgets.map(config => {
          const WidgetComponent = config.component;
          const widgetHeight = getWidgetHeight(config.height, compactMode);
          return (
            <Grid item xs={12} md={config.span} key={config.id}>
              <Box sx={{ height: widgetHeight }}>
                <WidgetComponent
                  selectedFacility={selectedFacility}
                  compactMode={compactMode}
                  data={dashboardData}
                  isRefreshing={dashboardIsRefreshing}
                  onRefresh={refreshDashboardData}
                  height={widgetHeight}
                  onCommsSinceHoursChange={config.id === 'driver-comms' ? setCommsSinceHours : undefined}
                  onBolSinceHoursChange={config.id === 'bol-dashboard' ? setBolSinceHours : undefined}
                />
              </Box>
            </Grid>
          );
        })}
      </Grid>

      {enabledWidgets.length === 0 && (
        <Paper sx={{ p: 6, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary">
            No widgets enabled
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Use the settings button above to enable widgets
          </Typography>
        </Paper>
      )}
    </div>
  );
};

export default Dashboard; 